import React from 'react';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { CoreLink, DynamicMediaItem } from '@uncinc/react-drupal-core';
import { Icon } from '@uncinc/uncinc-react-kitchen-sink';

import { ProfileDefault } from '../../../../types/uncinc_profile/profile';
import useBubble from '../../../../helpers/useBubble';

import './style.scss';

interface ProfileProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<ProfileDefault>;
  classes: string;
}

const ProfileTeaser: React.FC<ProfileProps> = ({ entity, classes, ...props }) => {
  const { t } = useTranslation();
  const profile = entity.toJS();
  const profileName = [
    profile.field_first_name[0].value,
    profile.field_last_name[0]?.value,
  ].join(' ');

  const profileFunction = profile.field_function?.[0]?.entity.name[0].value as string;
  const profileImage = profile.field_media_item[0];
  const profileLink = profile.field_link?.[0];
  const linkUri = profileLink?.uri;

  const getAriaLabel = () => {
    if (linkUri?.includes('linkedin')) return `${t('pages.see_linkedIn_profile')} ${profileName}`;
    if (linkUri) return `${t('pages.see_profile')} ${profileName}`;
    return '';
  };

  const {
    BubbleComponent,
    parentRef,
    onEnter,
    onLeave,
  } = useBubble();

  const slug = profileName.replace(/ /g, '-');

  return (
    <div className={classNames('ProfileTeaser', classes)} {...props}>
      <div
        ref={parentRef}
        id={slug}
        className="ProfileTeaser__image"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {profileImage && (
          <DynamicMediaItem
            media={profileImage}
            imageAspectRatio="1_1"
          />
        )}
        <BubbleComponent />
        <div className="ProfileTeaser__content">
          <h3 className="ProfileTeaser__name">{profileName}</h3>
          <span className="ProfileTeaser__function">{profileFunction}</span>
          {linkUri && (
            <CoreLink
              className="ProfileTeaser__link"
              href={linkUri}
              ariaLabel={getAriaLabel()}
            >
              <Icon name={linkUri.includes('linkedin') ? 'linkedin' : 'north_east'} />
            </CoreLink>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileTeaser;
